import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { CompanyService } from '../services/companyService';
import { InvitationService } from '../services/invitationService';
import { Company, CompanyMember, UserRole } from '../types/company';
import DatabaseInspector from './DatabaseInspector';
import ViewNicknames from './ViewNicknames';
import './MasterControlPanel.css';

interface MasterControlPanelProps {
  currentUser: User;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  onClose: () => void;
}

interface PlatformStats {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
  totalContracts: number;
}

interface CompanyWithMembers extends Company {
  members: CompanyMember[];
  memberCount: number;
  adminEmail?: string;
}

const MasterControlPanel: React.FC<MasterControlPanelProps> = ({ 
  currentUser, 
  showToast,
  onClose 
}) => {
  const [companies, setCompanies] = useState<CompanyWithMembers[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithMembers | null>(null);
  const [stats, setStats] = useState<PlatformStats>({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalContracts: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [showGenerateCode, setShowGenerateCode] = useState(false);
  const [showDatabaseInspector, setShowDatabaseInspector] = useState(false);
  const [showViewNicknames, setShowViewNicknames] = useState(false);
  
  // Form states
  const [companyForm, setCompanyForm] = useState({
    name: '',
    description: '',
    industry: '',
    website: ''
  });
  
  const [userAssignmentForm, setUserAssignmentForm] = useState({
    email: '',
    role: 'user' as UserRole
  });

  const [codeGenerationForm, setCodeGenerationForm] = useState({
    type: 'single-use' as 'single-use' | 'multi-use',
    restrictedEmail: '',
    assignedRole: 'user' as UserRole,
    expiresInDays: 30,
    maxUses: 1,
    notes: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCompanies(),
        loadPlatformStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const allCompanies = await CompanyService.getAllCompanies();
      console.log('All companies loaded:', allCompanies);
      
      // Load members for each company
      const companiesWithMembers = await Promise.all(
        allCompanies.map(async (company) => {
          console.log('Loading members for company:', company.id, company.name);
          const members = await CompanyService.getCompanyMembers(company.id);
          console.log('Members found for', company.name, ':', members.length, members);
          
          const adminMember = members.find(m => m.role === 'admin' || m.role === 'owner');
          
          return {
            ...company,
            members,
            memberCount: members.length,
            adminEmail: adminMember?.userId // Will need to fetch email separately
          };
        })
      );
      
      console.log('Companies with members:', companiesWithMembers);
      setCompanies(companiesWithMembers);
    } catch (error) {
      console.error('Error loading companies:', error);
      throw error;
    }
  };

  const loadPlatformStats = async () => {
    try {
      // Calculate platform statistics
      const allCompanies = await CompanyService.getAllCompanies();
      const activeCompanies = allCompanies.filter(c => c.isActive);
      
      let totalUsers = 0;
      let totalContracts = 0;
      
      for (const company of allCompanies) {
        const members = await CompanyService.getCompanyMembers(company.id);
        totalUsers += members.length;
        
        // Count contracts directly instead of relying on stats
        const contracts = await CompanyService.getCompanyContracts(company.id);
        totalContracts += contracts.length;
      }
      
      setStats({
        totalCompanies: allCompanies.length,
        activeCompanies: activeCompanies.length,
        totalUsers,
        totalContracts
      });
    } catch (error) {
      console.error('Error loading platform stats:', error);
    }
  };

  const handleCreateCompany = async () => {
    if (!companyForm.name.trim()) {
      showToast('Company name is required', 'error');
      return;
    }

    try {
      const newCompany = await CompanyService.createCompany({
        name: companyForm.name.trim(),
        description: companyForm.description.trim(),
        industry: companyForm.industry.trim(),
        website: companyForm.website.trim(),
        createdBy: currentUser.uid,
        settings: {
          allowUserRegistration: true,
          requireEmailVerification: true,
          defaultUserRole: 'user',
        },
        isActive: true
      });

      // Add master as owner
      await CompanyService.addCompanyMember({
        companyId: newCompany.id,
        userId: currentUser.uid,
        role: 'owner',
        status: 'active',
        invitedBy: currentUser.uid,
        invitedAt: new Date().toISOString(),
        permissions: {
          canCreateContracts: true,
          canEditAllContracts: true,
          canDeleteContracts: true,
          canManageUsers: true,
          canManageCompany: true,
          canViewAnalytics: true,
          canExportData: true
        }
      });

      showToast(`Company "${companyForm.name}" created successfully!`, 'success');
      setShowCreateCompany(false);
      setCompanyForm({ name: '', description: '', industry: '', website: '' });
      await loadAllData();
    } catch (error) {
      console.error('Error creating company:', error);
      showToast('Error creating company', 'error');
    }
  };

  const handleEditCompany = async () => {
    if (!selectedCompany || !companyForm.name.trim()) {
      showToast('Company name is required', 'error');
      return;
    }

    try {
      await CompanyService.updateCompany(selectedCompany.id, {
        name: companyForm.name.trim(),
        description: companyForm.description.trim(),
        industry: companyForm.industry.trim(),
        website: companyForm.website.trim()
      });

      showToast('Company updated successfully!', 'success');
      setShowEditCompany(false);
      setSelectedCompany(null);
      await loadAllData();
    } catch (error) {
      console.error('Error updating company:', error);
      showToast('Error updating company', 'error');
    }
  };

  const handleDeleteCompany = async (company: CompanyWithMembers) => {
    const confirmed = window.confirm(
      `Are you sure you want to DELETE "${company.name}"?\n\n` +
      `This will remove:\n` +
      `- ${company.memberCount} users\n` +
      `- All contracts\n` +
      `- All company data\n\n` +
      `This action CANNOT be undone!`
    );

    if (!confirmed) return;

    try {
      await CompanyService.deleteCompany(company.id);
      showToast(`Company "${company.name}" deleted successfully`, 'success');
      await loadAllData();
      
      if (selectedCompany?.id === company.id) {
        setSelectedCompany(null);
        setShowCompanyDetails(false);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      showToast('Error deleting company', 'error');
    }
  };

  const handleAssignUser = async () => {
    if (!selectedCompany || !userAssignmentForm.email.trim()) {
      showToast('Email is required', 'error');
      return;
    }

    try {
      // For now, we'll create an invitation
      // In a full implementation, you'd search for existing users
      await CompanyService.inviteUserToCompany({
        companyId: selectedCompany.id,
        email: userAssignmentForm.email.trim(),
        role: userAssignmentForm.role,
        invitedBy: currentUser.uid
      });

      showToast(`Invitation sent to ${userAssignmentForm.email}`, 'success');
      setShowAssignUser(false);
      setUserAssignmentForm({ email: '', role: 'user' });
      await loadAllData();
    } catch (error) {
      console.error('Error assigning user:', error);
      showToast('Error assigning user', 'error');
    }
  };

  const handleRemoveUser = async (member: CompanyMember) => {
    if (!selectedCompany) return;

    const confirmed = window.confirm(
      `Remove ${member.userId} from ${selectedCompany.name}?`
    );

    if (!confirmed) return;

    try {
      await CompanyService.removeCompanyMember(selectedCompany.id, member.userId);
      showToast('User removed successfully', 'success');
      await loadAllData();
      
      // Refresh selected company
      const updatedCompany = companies.find(c => c.id === selectedCompany.id);
      if (updatedCompany) {
        setSelectedCompany(updatedCompany);
      }
    } catch (error) {
      console.error('Error removing user:', error);
      showToast('Error removing user', 'error');
    }
  };

  const handleChangeUserRole = async (member: CompanyMember, newRole: UserRole) => {
    if (!selectedCompany) return;

    try {
      await CompanyService.updateCompanyMember(selectedCompany.id, member.userId, {
        role: newRole
      });
      showToast('User role updated successfully', 'success');
      await loadAllData();
      
      // Refresh selected company
      const updatedCompany = companies.find(c => c.id === selectedCompany.id);
      if (updatedCompany) {
        setSelectedCompany(updatedCompany);
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      showToast('Error updating user role', 'error');
    }
  };

  const openCompanyDetails = async (company: CompanyWithMembers) => {
    setSelectedCompany(company);
    setShowCompanyDetails(true);
  };

  const openEditCompany = (company: CompanyWithMembers) => {
    setSelectedCompany(company);
    setCompanyForm({
      name: company.name,
      description: company.description || '',
      industry: company.industry || '',
      website: company.website || ''
    });
    setShowEditCompany(true);
  };

  const handleGenerateCode = async () => {
    if (!selectedCompany) return;

    try {
      const options: any = {
        companyId: selectedCompany.id,
        assignedRole: codeGenerationForm.assignedRole,
        expiresInDays: codeGenerationForm.expiresInDays > 0 ? codeGenerationForm.expiresInDays : undefined,
        notes: codeGenerationForm.notes || `Invitation for ${selectedCompany.name} - ${codeGenerationForm.assignedRole}`
      };

      if (codeGenerationForm.type === 'multi-use' && codeGenerationForm.maxUses > 1) {
        options.maxUses = codeGenerationForm.maxUses;
      }

      if (codeGenerationForm.restrictedEmail.trim()) {
        options.restrictedEmail = codeGenerationForm.restrictedEmail.trim();
      }

      const code = await InvitationService.createInvitationCode(
        currentUser.uid,
        codeGenerationForm.type,
        options
      );

      // Copy code to clipboard and show success message
      navigator.clipboard.writeText(code.code);
      showToast(`✓ Code generated and copied: ${code.code}`, 'success');
      
      // Handle invitation email if email is provided
      if (codeGenerationForm.restrictedEmail.trim()) {
        try {
          const { EmailService } = await import('../services/emailService');
          
          // Show options for sending email
          const emailOptions = `
📧 Invitation Code Generated Successfully!

Code: ${code.code}
Recipient: ${codeGenerationForm.restrictedEmail.trim()}
Company: ${selectedCompany.name}

Choose how to send the invitation:

1. Open Email Client (recommended)
2. Copy Email Content
3. Copy Invitation Code Only

Click OK to open email client, or Cancel to copy code only.`;
          
          const openEmailClient = window.confirm(emailOptions);
          
          if (openEmailClient) {
            const emailOpened = EmailService.sendInvitationEmail(
              code,
              codeGenerationForm.restrictedEmail.trim(),
              selectedCompany.name,
              currentUser.displayName || currentUser.email || 'System Administrator',
              'https://tepproj.web.app'
            );
            
            if (emailOpened) {
              showToast('📧 Email client should be opening... Click SEND to deliver the invitation!', 'info');
            } else {
              showToast('⚠️ Could not open email client. Copying invitation details instead...', 'warning');
              EmailService.copyInvitationDetails(
                code,
                codeGenerationForm.restrictedEmail.trim(),
                selectedCompany.name,
                currentUser.displayName || currentUser.email || 'System Administrator',
                'https://tepproj.web.app'
              );
              showToast('📋 Invitation details copied to clipboard! Paste into your email.', 'success');
            }
          } else {
            // Copy invitation details to clipboard
            EmailService.copyInvitationDetails(
              code,
              codeGenerationForm.restrictedEmail.trim(),
              selectedCompany.name,
              currentUser.displayName || currentUser.email || 'System Administrator',
              'https://tepproj.web.app'
            );
            showToast('Invitation details copied to clipboard! Paste into your email.', 'success');
          }
        } catch (emailError) {
          console.error('Error handling invitation email:', emailError);
          showToast('Code generated! Please send invitation manually.', 'warning');
        }
      }
      
      setShowGenerateCode(false);
      setCodeGenerationForm({
        type: 'single-use',
        restrictedEmail: '',
        assignedRole: 'user',
        expiresInDays: 30,
        maxUses: 1,
        notes: ''
      });
    } catch (error) {
      console.error('Error generating code:', error);
      showToast('Error generating invitation code', 'error');
    }
  };

  const handleSendQuickInvitation = async () => {
    const recipientEmail = prompt('Enter recipient email address:');
    if (!recipientEmail || !recipientEmail.trim()) return;

    const role = prompt('Enter role for this user (admin/manager/user):', 'user');
    if (!role) return;

    try {
      const { EmailService } = await import('../services/emailService');
      
      // Create a temporary invitation code for email sending
      const tempCode = {
        id: 'temp-' + Date.now(),
        code: InvitationService.generateCode(),
        type: 'single-use' as const,
        status: 'active' as const,
        createdBy: currentUser.uid,
        createdAt: new Date() as any,
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) as any, // 30 days
        usedCount: 0
      };

      const openEmailClient = window.confirm(`
📧 Quick Invitation Email

Recipient: ${recipientEmail.trim()}
Company: ${selectedCompany?.name || 'Unknown'}
Role: ${role}
Code: ${tempCode.code}

Click OK to open email client with invitation.`);
      
      if (openEmailClient) {
        EmailService.sendInvitationEmail(
          tempCode,
          recipientEmail.trim(),
          selectedCompany?.name || 'Company',
          currentUser.displayName || currentUser.email || 'System Administrator',
          'https://tepproj.web.app'
        );
        showToast('Email client opened! Please click SEND to deliver the invitation.', 'info');
        showToast(`Don't forget to create the actual invitation code: ${tempCode.code}`, 'warning');
      }
    } catch (error) {
      console.error('Error sending quick invitation:', error);
      showToast('Error sending invitation email', 'error');
    }
  };

  if (loading) {
    return (
      <div className="master-control-panel">
        <div className="loading">Loading platform data...</div>
      </div>
    );
  }

  return (
    <div className="master-control-panel">
      <div className="panel-header">
        <h1>🏢 MASTER CONTROL PANEL</h1>
        <button className="btn-back-dashboard" onClick={onClose}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Platform Statistics */}
      <div className="platform-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.totalCompanies}</div>
          <div className="stat-label">Total Companies</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.activeCompanies}</div>
          <div className="stat-label">Active Companies</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalUsers}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalContracts}</div>
          <div className="stat-label">Total Contracts</div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <button 
          className="btn-primary"
          onClick={() => setShowCreateCompany(true)}
        >
          ➕ Create New Company
        </button>
        <button 
          className="btn-secondary"
          onClick={() => setShowViewNicknames(true)}
        >
          👥 View Nicknames
        </button>
        <button 
          className="btn-secondary"
          onClick={() => setShowDatabaseInspector(true)}
        >
          🔍 Debug Database
        </button>
      </div>

      {/* Companies List */}
      <div className="companies-section">
        <h2>All Companies ({companies.length})</h2>
        <div className="companies-grid">
          {companies.map((company) => (
            <div key={company.id} className="company-card">
              <div className="company-card-header">
                <h3>{company.name}</h3>
                <span className={`status-badge ${company.isActive ? 'active' : 'inactive'}`}>
                  {company.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="company-card-body">
                <div className="company-info">
                  <p>
                    <strong>ID:</strong>{' '}
                    <code style={{
                      background: '#ecf0f1',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(company.id);
                      showToast('Company ID copied!', 'success');
                    }}
                    title="Click to copy ID"
                    >
                      {company.id.substring(0, 12)}...
                    </code>
                  </p>
                  <p><strong>Industry:</strong> {company.industry || 'Not specified'}</p>
                  <p><strong>Users:</strong> {company.memberCount}</p>
                  <p><strong>Created:</strong> {company.createdAt ? 
                    (company.createdAt.toDate ? 
                      company.createdAt.toDate().toLocaleDateString() : 
                      new Date(company.createdAt).toLocaleDateString()
                    ) : 'Unknown'}</p>
                </div>
              </div>
              
              <div className="company-card-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => openCompanyDetails(company)}
                >
                  👥 Users
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openEditCompany(company)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => handleDeleteCompany(company)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {companies.length === 0 && (
          <div className="empty-state">
            <p>No companies created yet.</p>
            <button 
              className="btn-primary"
              onClick={() => setShowCreateCompany(true)}
            >
              Create Your First Company
            </button>
          </div>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateCompany && (
        <div className="modal-overlay" onClick={() => setShowCreateCompany(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Company</h2>
              <button className="btn-close" onClick={() => setShowCreateCompany(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                  placeholder="ABC Trading Company"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={companyForm.description}
                  onChange={(e) => setCompanyForm({...companyForm, description: e.target.value})}
                  placeholder="Brief description of the company"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={companyForm.industry}
                  onChange={(e) => setCompanyForm({...companyForm, industry: e.target.value})}
                  placeholder="Manufacturing, Trading, etc."
                />
              </div>
              
              <div className="form-group">
                <label>Website</label>
                <input
                  type="text"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateCompany(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateCompany}>
                Create Company
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompany && selectedCompany && (
        <div className="modal-overlay" onClick={() => setShowEditCompany(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Company</h2>
              <button className="btn-close" onClick={() => setShowEditCompany(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={companyForm.description}
                  onChange={(e) => setCompanyForm({...companyForm, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={companyForm.industry}
                  onChange={(e) => setCompanyForm({...companyForm, industry: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Website</label>
                <input
                  type="text"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditCompany(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleEditCompany}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Details Modal */}
      {showCompanyDetails && selectedCompany && (
        <div className="modal-overlay" onClick={() => setShowCompanyDetails(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCompany.name} - Details</h2>
              <button className="btn-close" onClick={() => setShowCompanyDetails(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="company-details-section">
                <h3>Company Information</h3>
                <p>
                  <strong>Company ID:</strong>{' '}
                  <code style={{
                    background: '#ecf0f1',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#2c3e50',
                    cursor: 'pointer',
                    userSelect: 'all'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedCompany.id);
                    showToast('Company ID copied to clipboard!', 'success');
                  }}
                  title="Click to copy"
                  >
                    {selectedCompany.id}
                  </code>
                  {' '}
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(selectedCompany.id);
                      showToast('Company ID copied!', 'success');
                    }}
                    style={{
                      background: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      marginLeft: '8px'
                    }}
                  >
                    📋 Copy
                  </button>
                </p>
                <p><strong>Description:</strong> {selectedCompany.description || 'Not provided'}</p>
                <p><strong>Industry:</strong> {selectedCompany.industry || 'Not specified'}</p>
                <p><strong>Website:</strong> {selectedCompany.website || 'Not provided'}</p>
                <p><strong>Status:</strong> {selectedCompany.isActive ? 'Active' : 'Inactive'}</p>
                <p><strong>Created:</strong> {selectedCompany.createdAt ? 
                  (selectedCompany.createdAt.toDate ? 
                    selectedCompany.createdAt.toDate().toLocaleDateString() : 
                    new Date(selectedCompany.createdAt).toLocaleDateString()
                  ) : 'Unknown'}</p>
              </div>

              <div className="company-details-section">
                <div className="section-header">
                  <h3>Assigned Users ({selectedCompany.members.length})</h3>
                  <div>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowAssignUser(true)}
                      style={{marginRight: '10px'}}
                    >
                      ➕ Assign User
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => setShowGenerateCode(true)}
                    >
                      🔑 Generate Code
                    </button>
                  </div>
                </div>

                <div className="users-list">
                  {selectedCompany.members.map((member) => (
                    <div key={member.id} className="user-row">
                      <div className="user-info">
                        <div className="user-email">{member.userId}</div>
                        <div className="user-meta">
                          Joined: {new Date(member.invitedAt).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="user-actions">
                        <select
                          value={member.role}
                          onChange={(e) => handleChangeUserRole(member, e.target.value as UserRole)}
                          className="role-select"
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="user">User</option>
                        </select>
                        
                        <button
                          className="btn-danger-small"
                          onClick={() => handleRemoveUser(member)}
                          title="Remove user"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}

                  {selectedCompany.members.length === 0 && (
                    <div className="empty-state-small">
                      No users assigned yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCompanyDetails(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign User Modal */}
      {showAssignUser && selectedCompany && (
        <div className="modal-overlay" onClick={() => setShowAssignUser(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign User to {selectedCompany.name}</h2>
              <button className="btn-close" onClick={() => setShowAssignUser(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>User Email *</label>
                <input
                  type="email"
                  value={userAssignmentForm.email}
                  onChange={(e) => setUserAssignmentForm({...userAssignmentForm, email: e.target.value})}
                  placeholder="user@example.com"
                />
              </div>
              
              <div className="form-group">
                <label>Role</label>
                <select
                  value={userAssignmentForm.role}
                  onChange={(e) => setUserAssignmentForm({...userAssignmentForm, role: e.target.value as UserRole})}
                >
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="user">User</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAssignUser(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAssignUser}>
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Invitation Code Modal */}
      {showGenerateCode && selectedCompany && (
        <div className="modal-overlay" onClick={() => setShowGenerateCode(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔑 Generate Invitation Code</h2>
              <button className="btn-close" onClick={() => setShowGenerateCode(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Company (Auto-Selected)</label>
                <input
                  type="text"
                  value={selectedCompany.name}
                  disabled
                  style={{background: '#f0f0f0', cursor: 'not-allowed'}}
                />
                <small style={{color: '#7f8c8d', marginTop: '4px', display: 'block'}}>
                  Company ID: {selectedCompany.id}
                </small>
              </div>

              <div className="form-group">
                <label>Code Type</label>
                <select
                  value={codeGenerationForm.type}
                  onChange={(e) => setCodeGenerationForm({
                    ...codeGenerationForm, 
                    type: e.target.value as 'single-use' | 'multi-use'
                  })}
                >
                  <option value="single-use">Single Use (1 person)</option>
                  <option value="multi-use">Multi Use (Multiple people)</option>
                </select>
              </div>

              {codeGenerationForm.type === 'multi-use' && (
                <div className="form-group">
                  <label>Maximum Uses</label>
                  <input
                    type="number"
                    min="2"
                    max="100"
                    value={codeGenerationForm.maxUses}
                    onChange={(e) => setCodeGenerationForm({
                      ...codeGenerationForm,
                      maxUses: parseInt(e.target.value)
                    })}
                  />
                </div>
              )}
              
              <div className="form-group">
                <label>Assigned Role in Company</label>
                <select
                  value={codeGenerationForm.assignedRole}
                  onChange={(e) => setCodeGenerationForm({
                    ...codeGenerationForm,
                    assignedRole: e.target.value as UserRole
                  })}
                >
                  <option value="admin">Admin (Full access)</option>
                  <option value="manager">Manager (Can manage contracts)</option>
                  <option value="user">User (Basic access)</option>
                </select>
                <small style={{color: '#7f8c8d', marginTop: '4px', display: 'block'}}>
                  Users will be assigned this role in {selectedCompany.name}
                </small>
              </div>

              <div className="form-group">
                <label>📧 Send Invitation Email To (Optional)</label>
                <input
                  type="email"
                  value={codeGenerationForm.restrictedEmail}
                  onChange={(e) => setCodeGenerationForm({
                    ...codeGenerationForm,
                    restrictedEmail: e.target.value
                  })}
                  placeholder="user@example.com (leave empty for manual sharing)"
                />
                <small style={{color: '#7f8c8d', marginTop: '4px', display: 'block'}}>
                  If provided: Only this email can use the code + invitation email will be sent automatically
                </small>
              </div>

              <div className="form-group">
                <label>Expires In (Days)</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={codeGenerationForm.expiresInDays}
                  onChange={(e) => setCodeGenerationForm({
                    ...codeGenerationForm,
                    expiresInDays: parseInt(e.target.value)
                  })}
                />
                <small style={{color: '#7f8c8d', marginTop: '4px', display: 'block'}}>
                  0 = Never expires
                </small>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={codeGenerationForm.notes}
                  onChange={(e) => setCodeGenerationForm({
                    ...codeGenerationForm,
                    notes: e.target.value
                  })}
                  placeholder="Internal notes about this invitation"
                  rows={3}
                />
              </div>

              <div className="info-box" style={{
                background: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '4px',
                padding: '12px',
                marginTop: '15px'
              }}>
                <strong>📋 Summary:</strong>
                <ul style={{margin: '8px 0 0 20px', fontSize: '14px'}}>
                  <li>Company: <strong>{selectedCompany.name}</strong></li>
                  <li>Role: <strong>{codeGenerationForm.assignedRole}</strong></li>
                  <li>Type: <strong>{codeGenerationForm.type}</strong></li>
                  {codeGenerationForm.restrictedEmail && (
                    <>
                      <li>Email: <strong>{codeGenerationForm.restrictedEmail}</strong></li>
                      <li>📧 <strong>Invitation email will be sent automatically</strong></li>
                    </>
                  )}
                  <li>When user signs up → Automatically joins {selectedCompany.name}</li>
                </ul>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowGenerateCode(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleGenerateCode}>
                🔑 Generate Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Nicknames Modal */}
      <ViewNicknames 
        isOpen={showViewNicknames}
        onClose={() => setShowViewNicknames(false)}
      />

      {/* Database Inspector Modal */}
      {showDatabaseInspector && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>🔍 Database Inspector</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDatabaseInspector(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <DatabaseInspector />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterControlPanel;

